import {payinAuditLabel} from "../utils/payinAudit";
import {ExternalCompletionBadge, ExternalCompletionDetails, completionPending} from "../components/ExternalCompletion";
import React, {useEffect, useMemo, useState} from "react";
import {ApiError, downloadReceipt, getOrderTimeline, getTraderHistory} from "../api";
import {useAuth} from "../auth";
import {OrderChatLink} from "../components/OrderChat";
import {useOrderChatPanel} from "../components/OrderChatCenter";
import {AttemptReceipts} from "../components";
import type {ActivityScope, AuditEventItem, HistoryAttempt, HistoryReceipt, OrderHistoryItem} from "../types";
import {formatAmount, formatDate, maskCard, statusClass} from "../utils";

const PAGE_LIMIT = 50;

function searchFromHash(): string | null {
    return new URLSearchParams(window.location.hash.split("?")[1] || "").get("q");
}

export function HistoryPage(): JSX.Element {
    const {token, user} = useAuth();
    const isAdmin = user?.roles?.includes("admin") ?? false;
    const [scope, setScope] = useState<ActivityScope>(isAdmin ? "all" : "self");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    // Free-text filter drafts (committed to applied filters below after a debounce).
    const [searchInput, setSearchInput] = useState(() => searchFromHash() || "");
    const [traderIdInput, setTraderIdInput] = useState("");
    const [teamIdInput, setTeamIdInput] = useState("");
    const [search, setSearch] = useState(searchInput);
    const [traderId, setTraderId] = useState("");
    const [teamId, setTeamId] = useState("");
    const [offset, setOffset] = useState(0);
    const [items, setItems] = useState<OrderHistoryItem[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [refreshRevision, setRefreshRevision] = useState(0);
    const waiting = items.some(item => item.attempts.some(attempt => completionPending(attempt.externalCompletion)));
    useEffect(() => {
        if (!waiting) return;
        const timer = setInterval(() => setRefreshRevision(value => value + 1), 5000);
        return () => clearInterval(timer);
    }, [waiting]);

    // Ссылка из уведомления меняет ?q= на уже открытой странице.
    useEffect(() => {
        const follow = (): void => {
            const q = searchFromHash();
            if (q !== null) setSearchInput(q);
        };
        window.addEventListener("hashchange", follow);
        return () => window.removeEventListener("hashchange", follow);
    }, []);

    useEffect(() => {
        setScope(isAdmin ? "all" : "self");
        setOffset(0);
    }, [isAdmin]);

    // Debounce free-text inputs so typing does not fire a request per keystroke
    // (and a half-typed uuid does not bounce off the backend's 400 validation).
    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setTraderId(traderIdInput.trim());
            setTeamId(teamIdInput.trim());
            setOffset(0);
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput, traderIdInput, teamIdInput]);

    useEffect(() => {
        let cancelled = false;

        if (!token) {
            setItems([]);
            setHasMore(false);
            setNextOffset(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        getTraderHistory(token, {
            scope,
            traderId: traderId || undefined,
            teamId: teamId || undefined,
            q: search || undefined,
            from: from || undefined,
            to: to || undefined,
            limit: PAGE_LIMIT,
            offset,
            sort: "desc",
        })
            .then((resp) => {
                if (cancelled) return;
                setItems(resp.items ?? []);
                setHasMore(resp.hasMore);
                setNextOffset(resp.nextOffset ?? null);
            })
            .catch((err) => {
                if (!cancelled) setError(formatHistoryError(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [from, offset, scope, search, teamId, to, token, traderId, refreshRevision]);

    const scopeOptions = useMemo(() => {
        if (isAdmin) return [{scope: "all", label: "Все"}] as Array<{scope: ActivityScope; label: string}>;
        const options: Array<{scope: ActivityScope; label: string}> = [
            {scope: "self", label: "Я"},
            {scope: "team", label: "Команда"},
        ];
        return options;
    }, [isAdmin]);

    const showingFrom = items.length === 0 ? 0 : offset + 1;
    const showingTo = offset + items.length;

    const updateScope = (next: ActivityScope): void => {
        setScope(next);
        setOffset(0);
    };

    const updateFrom = (value: string): void => {
        setFrom(value);
        setOffset(0);
    };

    const updateTo = (value: string): void => {
        setTo(value);
        setOffset(0);
    };

    const toggleExpanded = (orderId: string): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const handleReceiptDownload = async (receipt: HistoryReceipt): Promise<void> => {
        if (!receipt.receiptUrl) return;
        try {
            await downloadReceipt(receipt.receiptUrl, token, `${receipt.receiptId}.pdf`);
        } catch (err) {
            setError(formatHistoryError(err));
        }
    };

    return (
        <div className="page history-page">
            <div className="history-header">
                <h2>История заявок</h2>
                <p>{isAdmin ? "Все заявки с попытками; можно фильтровать по трейдеру, команде и датам." : "Заявки, которые вы вели — раскройте, чтобы увидеть все действия"}</p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <div className="history-toolbar activity-toolbar">
                <div className="history-filter-groups">
                    <div className="history-chip-group">
                        {scopeOptions.map((option) => (
                            <button
                                key={option.scope}
                                className={`history-filter-chip ${scope === option.scope ? "active" : ""}`}
                                onClick={() => updateScope(option.scope)}
                                type="button"
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <span className="history-filter-divider"/>
                    <label className="activity-date-filter">
                        <span>From</span>
                        <input type="date" value={from} onChange={(event) => updateFrom(event.target.value)}/>
                    </label>
                    <label className="activity-date-filter">
                        <span>To</span>
                        <input type="date" value={to} onChange={(event) => updateTo(event.target.value)}/>
                    </label>
                    <label className="activity-date-filter activity-search-filter">
                        <span>Поиск</span>
                        <input
                            type="search"
                            value={searchInput}
                            placeholder="Заявка, клиент или банк"
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                    </label>
                    {isAdmin && (
                        <>
                            <label className="activity-date-filter">
                                <span>Trader ID</span>
                                <input
                                    type="text"
                                    value={traderIdInput}
                                    placeholder="uuid"
                                    onChange={(event) => setTraderIdInput(event.target.value)}
                                />
                            </label>
                            <label className="activity-date-filter">
                                <span>Team ID</span>
                                <input
                                    type="text"
                                    value={teamIdInput}
                                    placeholder="uuid"
                                    onChange={(event) => setTeamIdInput(event.target.value)}
                                />
                            </label>
                        </>
                    )}
                </div>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setOffset(0)} disabled={loading}>
                    Обновить
                </button>
            </div>

            <section className="history-list-card activity-card">
                <div className="history-list-header">
                    <strong>Заявки</strong>
                    <span className="badge">{loading ? "..." : items.length}</span>
                </div>
                {items.length === 0 ? (
                    <div className="empty-state compact">{loading ? "Загрузка истории..." : "Заявок не найдено"}</div>
                ) : (
                    <div className="history-order-list">
                        {items.map((item) => (
                            <OrderRow
                                key={item.orderId}
                                item={item}
                                open={expanded.has(item.orderId)}
                                token={token}
                                onToggle={() => toggleExpanded(item.orderId)}
                                onDownloadReceipt={handleReceiptDownload}
                            />
                        ))}
                    </div>
                )}
                <div className="activity-pagination">
                    <span className="mono">
                        {showingFrom}-{showingTo}
                    </span>
                    <div>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={loading || offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                        >
                            Назад
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={loading || !hasMore}
                            onClick={() => setOffset(nextOffset ?? offset + PAGE_LIMIT)}
                        >
                            Далее
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}

function OrderRow({
    item,
    open,
    token,
    onToggle,
    onDownloadReceipt,
}: {
    item: OrderHistoryItem;
    open: boolean;
    token: string | null;
    onToggle: () => void;
    onDownloadReceipt: (receipt: HistoryReceipt) => void;
}): JSX.Element {
    const {openChat, closeChat, orderID, placement} = useOrderChatPanel();
    const chatOpen = orderID === item.orderId && placement === "history";
    useEffect(() => {
        if (chatOpen && !open) closeChat();
    }, [chatOpen, open, closeChat]);
    return (
        <div className={`history-order ${open ? "open" : ""}`}>
            <button className="history-order-summary" type="button" onClick={onToggle} aria-expanded={open}>
                <span className="history-order-caret">{open ? "▾" : "▸"}</span>
                <span className="history-order-remote mono">{item.remoteId}</span>
                <span className="history-order-merchant" title={item.merchantName || undefined}>
                    {item.merchantName || "—"}
                </span>
                <span className={`details-dir-badge ${item.side}`}>{item.side.toUpperCase()}</span>
                <span className="history-order-bank">{item.bankId}</span>
                <span className="history-order-amount mono">{formatAmount(item.amount)} ₽</span>
                <span className={`payment-status ${statusClass(item.orderStatus)}`}>{item.orderStatus}</span>
                <span className="history-order-attempts">{item.attemptsCount} поп.</span>
                <span className="history-order-date mono">{formatDate(item.lastActivityAt)}</span>
            </button>
            {open && (
                <HistoryChatDetails open={chatOpen} label={item.remoteId} onClose={closeChat}>
                <div className="history-order-detail">
                    <dl className="history-order-meta">
                        <div><dt>Клиент</dt><dd>{item.clientFio || "—"}</dd></div>
                        <div><dt>Карта</dt><dd className="mono">{maskCard(item.clientCard)}</dd></div>
                        <div><dt>Сумма</dt><dd className="mono">{formatAmount(item.amount)} ₽</dd></div>
                        <div><dt>Создана</dt><dd className="mono">{formatDate(item.createdAt)}</dd></div>
                    </dl>
                    <OrderTimeline orderId={item.orderId} open={open} token={token}/>
                    {<div className="details-action-row"><span className="order-chat-action"><OrderChatLink orderID={item.orderId} onOpen={(id) => openChat(id, "history")}/></span></div>}
                    <table className="activity-table history-attempts-table">
                        <thead>
                        <tr>
                            <th>Взял</th>
                            <th>Чек</th>
                            <th>Завершил</th>
                            <th>Статус</th>
                            <th>Сумма</th>
                            <th>Курс</th>
                            <th>Трейдер</th>
                        </tr>
                        </thead>
                        <tbody>
                        {item.attempts.map((attempt) => (
                            <AttemptRow key={attempt.paymentId} attempt={attempt} token={token} onDownloadReceipt={onDownloadReceipt}/>
                        ))}
                        </tbody>
                    </table>
                </div>
                </HistoryChatDetails>
            )}
        </div>
    );
}

function HistoryChatDetails({open, label, onClose, children}: {open: boolean; label: string; onClose: () => void; children: React.ReactNode}): JSX.Element {
    if (!open) return <>{children}</>;
    return <div className="details-backdrop" onClick={onClose}>
        <aside className="details-drawer history-chat-details" aria-label={`Заявка ${label}`} onClick={event => event.stopPropagation()}>
            <header className="order-chat-inline-header"><strong>Заявка {label}</strong><button className="details-close" type="button" aria-label="Закрыть заявку" onClick={onClose}>×</button></header>
            <div className="history-chat-details-body">{children}</div>
        </aside>
    </div>;
}

function AttemptRow({
    token,
    attempt,
    onDownloadReceipt,
}: {
    attempt: HistoryAttempt;
    token: string | null;
    onDownloadReceipt: (receipt: HistoryReceipt) => void;
}): JSX.Element {
    const {user} = useAuth();
    const canRead = user?.roles?.includes("admin") || user?.reference_id === attempt.traderId;
    return (
        <tr>
            <td className="mono" data-label="Взял">{formatDate(attempt.claimedAt)}</td>
            <td data-label="Чек">
                <AttemptReceipts attempt={attempt} onDownload={onDownloadReceipt}/>
            </td>
            <td className="mono" data-label="Завершил">{formatDate(attempt.finishedAt)}</td>
            <td data-label="Статус">
                <span className={`payment-status ${statusClass(attempt.status)}`}>{attempt.status}</span>
                {canRead ? <ExternalCompletionDetails paymentId={attempt.paymentId} completion={attempt.externalCompletion} token={token} canConfirm={user?.roles?.includes("admin") ?? false}/> : <ExternalCompletionBadge completion={attempt.externalCompletion}/>}
            </td>
            <td className="mono" data-label="Сумма">{attempt.amountReal == null ? "—" : `${formatAmount(attempt.amountReal)} ₽`}</td>
            <td className="mono" data-label="Курс">{attempt.currencyRate ?? "—"}</td>
            <td data-label="Трейдер">{attempt.traderName || "—"}</td>
        </tr>
    );
}

function OrderTimeline({orderId, open, token}: {orderId: string; open: boolean; token: string | null}): JSX.Element {
    const [items, setItems] = useState<AuditEventItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!open || !token) return;
        setLoading(true);
        getOrderTimeline(token, orderId)
            .then((resp) => {
                if (!cancelled) setItems(resp.items ?? []);
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, orderId, token]);

    if (loading && items.length === 0) {
        return <div className="empty-state compact">Loading timeline...</div>;
    }
    if (items.length === 0) {
        return <div className="empty-state compact">No audit timeline yet</div>;
    }
    return (
        <div className="activity-table-wrap">
            <table className="activity-table history-attempts-table">
                <thead>
                <tr>
                    <th>time</th>
                    <th>event</th>
                    <th>actor</th>
                    <th>context</th>
                </tr>
                </thead>
                <tbody>
                {items.map((event) => (
                    <tr key={event.id}>
                        <td className="mono" data-label="time">{formatDate(event.occurredAt)}</td>
                        <td data-label="event">{payinAuditLabel(event.payload?.action) ?? eventLabel(event.eventType)}</td>
                        <td data-label="actor">{event.actorLabel || event.traderName || event.actorType}</td>
                        <td className="mono" data-label="context">{timelineContext(event)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}

const EVENT_LABELS: Record<string, string> = {
    order_created: "Order created",
    order_pooled: "Entered pool",
    order_claimed: "Claimed",
    receipt_uploaded: "Receipt uploaded",
    payment_completed: "Paid",
    payment_cancelled: "Cancelled",
    payment_appealed: "Appeal",
    payment_expired: "Expired",
    appeal_resolved: "Appeal resolved",
    order_partially_filled: "Partially filled",
    order_succeeded: "Order succeeded",
    payin_requisites_set: "Реквизиты приёма введены",
    // Сигналы клиента заявки-обмена. Два разных факта и две разных подписи:
    // отметка приходит один раз и без файла, чек — сколько угодно раз и с
    // файлом, и разбор обязан отличать их с первого взгляда.
    client_marked_paid: "Клиент отметил оплату",
    client_receipt_uploaded: "Клиент приложил чек",
    // Второй успех заявки-обмена, и не денежный: рубли провёл
    // payment_completed, а здесь закрыт перевод USDT клиенту. Подпись
    // называет именно этап, а не «заявка завершена», — иначе в таймлайне
    // подряд стояли бы два одинаковых по смыслу события.
    exchange_payout_confirmed: "USDT-этап закрыт",
};

function eventLabel(type: string): string {
    return EVENT_LABELS[type] ?? type;
}

function payinRequisitesSummary(event: AuditEventItem): string {
    if (event.eventType !== "payin_requisites_set" || !event.payload) return "";
    const bankName = typeof event.payload.bankName === "string" ? event.payload.bankName : "";
    const cardMask = typeof event.payload.cardMask === "string" ? event.payload.cardMask : "";
    const holder = event.payload.cardHolderPresent ? "ФИО указано" : "";
    return [bankName, cardMask, holder].filter(Boolean).join(" ");
}

function timelineContext(event: AuditEventItem): string {
    const status = event.toStatus ? `${event.fromStatus ?? "-"} -> ${event.toStatus}` : "";
    const amount = event.amountReal == null ? "" : `${formatAmount(event.amountReal)} RUB`;
    const rate = event.currencyRate ? `rate ${event.currencyRate}` : "";
    const requisites = payinRequisitesSummary(event);
    return [status, amount, rate, requisites].filter(Boolean).join(" | ") || "-";
}

function formatHistoryError(err: unknown): string {
    if (err instanceof ApiError) {
        if (err.status === 401) return "Требуется вход.";
        if (err.status === 403) return "Эта область истории недоступна.";
        return err.message;
    }
    if (err instanceof Error) return err.message;
    return "Не удалось загрузить историю.";
}
